/**
 * DynamicSchemaForm
 *
 * A dynamic form component that renders form fields based on a JSON Schema.
 * Supports validation, error display, and default values.
 *
 * Validates: Requirements 5.1, 5.2, 5.3, 5.4, 5.5
 * - Requirement 5.1: WHEN an operator selects a plugin to configure, THE Console
 *   SHALL render a dynamic form based on the plugin's configuration schema.
 * - Requirement 5.2: THE Console SHALL validate plugin configuration inputs against
 *   the schema before saving.
 * - Requirement 5.3: IF configuration validation fails, THEN THE Console SHALL
 *   display field-level error messages.
 * - Requirement 5.4: THE Console SHALL provide default values for optional
 *   configuration fields.
 * - Requirement 5.5: WHEN configuration is saved successfully, THE Console SHALL
 *   display a confirmation message.
 */

import React, { useCallback, useMemo } from 'react';
import { Input } from '../../design-system';
import { tokens } from '../../design-system/tokens';

// ============================================
// TYPES
// ============================================

/**
 * JSON Schema 7 type definition (simplified for form rendering)
 */
export interface JSONSchema7 {
  type?: string | string[];
  properties?: Record<string, JSONSchema7>;
  required?: string[];
  additionalProperties?: boolean | JSONSchema7;
  items?: JSONSchema7 | JSONSchema7[];
  enum?: unknown[];
  const?: unknown;
  default?: unknown;
  title?: string;
  description?: string;
  format?: string;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  [key: string]: unknown;
}

/**
 * Validation error for a specific field
 */
export interface FieldError {
  path: string;
  message: string;
}

/**
 * Props for the DynamicSchemaForm component
 */
export interface DynamicSchemaFormProps {
  /** JSON Schema defining the form structure */
  schema: JSONSchema7;
  /** Current form values */
  values: Record<string, unknown>;
  /** Callback when values change */
  onChange: (values: Record<string, unknown>) => void;
  /** Validation errors from the server or local validation */
  errors?: FieldError[];
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Whether to show labels for fields */
  showLabels?: boolean;
}

/**
 * Props for individual field components
 */
interface FieldProps {
  name: string;
  schema: JSONSchema7;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the primary type from a schema type definition
 */
function getPrimaryType(type: string | string[] | undefined): string {
  if (Array.isArray(type)) {
    // Filter out 'null' and return the first non-null type
    const nonNullTypes = type.filter((t) => t !== 'null');
    return nonNullTypes[0] || 'string';
  }
  return type || 'string';
}

/**
 * Get the default value for a field based on its schema
 */
function getDefaultValue(schema: JSONSchema7): unknown {
  if (schema.default !== undefined) {
    return schema.default;
  }

  const type = getPrimaryType(schema.type);
  switch (type) {
    case 'boolean':
      return false;
    case 'number':
    case 'integer':
      return schema.minimum ?? 0;
    case 'array':
      return [];
    case 'object':
      return {};
    case 'string':
    default:
      return '';
  }
}

/**
 * Get a human-readable label from a field name
 */
function getFieldLabel(name: string, schema: JSONSchema7): string {
  if (schema.title) {
    return schema.title;
  }
  // Convert camelCase or snake_case to Title Case
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

/**
 * Validate a single field value against its schema
 */
export function validateField(
  name: string,
  value: unknown,
  schema: JSONSchema7,
  required: boolean
): string | null {
  const type = getPrimaryType(schema.type);

  // Check required
  if (required) {
    if (value === undefined || value === null || value === '') {
      return `${getFieldLabel(name, schema)} es requerido`;
    }
  }

  // Skip validation for empty optional fields
  if (value === undefined || value === null || value === '') {
    return null;
  }

  // Type-specific validation
  switch (type) {
    case 'string': {
      if (typeof value !== 'string') {
        return 'Debe ser texto';
      }
      if (schema.minLength !== undefined && value.length < schema.minLength) {
        return `Debe tener al menos ${schema.minLength} caracteres`;
      }
      if (schema.maxLength !== undefined && value.length > schema.maxLength) {
        return `Debe tener como máximo ${schema.maxLength} caracteres`;
      }
      if (schema.pattern) {
        const regex = new RegExp(schema.pattern);
        if (!regex.test(value)) {
          return 'Formato inválido';
        }
      }
      if (schema.format === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Email inválido';
        }
      }
      if (schema.format === 'uri' || schema.format === 'url') {
        try {
          new URL(value);
        } catch {
          return 'URL inválida';
        }
      }
      break;
    }
    case 'number':
    case 'integer': {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (typeof numValue !== 'number' || isNaN(numValue)) {
        return 'Debe ser un número';
      }
      if (type === 'integer' && !Number.isInteger(numValue)) {
        return 'Debe ser un número entero';
      }
      if (schema.minimum !== undefined && numValue < schema.minimum) {
        return `Debe ser mayor o igual a ${schema.minimum}`;
      }
      if (schema.maximum !== undefined && numValue > schema.maximum) {
        return `Debe ser menor o igual a ${schema.maximum}`;
      }
      break;
    }
    case 'boolean': {
      if (typeof value !== 'boolean') {
        return 'Debe ser verdadero o falso';
      }
      break;
    }
  }

  // Enum validation
  if (schema.enum && !schema.enum.includes(value)) {
    return `Debe ser uno de: ${schema.enum.join(', ')}`;
  }

  return null;
}

/**
 * Validate all form values against the schema
 */
export function validateForm(
  values: Record<string, unknown>,
  schema: JSONSchema7
): FieldError[] {
  const errors: FieldError[] = [];
  const properties = schema.properties || {};
  const required = schema.required || [];

  for (const [name, fieldSchema] of Object.entries(properties)) {
    const error = validateField(name, values[name], fieldSchema, required.includes(name));
    if (error) {
      errors.push({ path: `/${name}`, message: error });
    }
  }

  return errors;
}

/**
 * Apply default values from schema to form values
 */
export function applyDefaults(
  values: Record<string, unknown>,
  schema: JSONSchema7
): Record<string, unknown> {
  const result = { ...values };
  const properties = schema.properties || {};

  for (const [name, fieldSchema] of Object.entries(properties)) {
    if (result[name] === undefined) {
      const defaultValue = getDefaultValue(fieldSchema);
      if (defaultValue !== undefined && defaultValue !== '') {
        result[name] = defaultValue;
      }
    }
  }

  return result;
}

// ============================================
// FIELD COMPONENTS
// ============================================

/**
 * Text input field
 */
function TextField({ name, schema, value, onChange, error, disabled, required }: FieldProps) {
  const label = getFieldLabel(name, schema);
  const inputType = schema.format === 'password' ? 'password' : 'text';

  return (
    <Input
      label={`${label}${required ? ' *' : ''}`}
      type={inputType}
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      helperText={schema.description}
      disabled={disabled}
      fullWidth
      placeholder={schema.description || `Ingrese ${label.toLowerCase()}`}
    />
  );
}

/**
 * Number input field
 */
function NumberField({ name, schema, value, onChange, error, disabled, required }: FieldProps) {
  const label = getFieldLabel(name, schema);

  return (
    <Input
      label={`${label}${required ? ' *' : ''}`}
      type="number"
      value={value !== undefined && value !== null ? String(value) : ''}
      onChange={(e) => {
        const val = e.target.value;
        if (val === '') {
          onChange(undefined);
        } else {
          const num = getPrimaryType(schema.type) === 'integer' ? parseInt(val, 10) : parseFloat(val);
          onChange(isNaN(num) ? val : num);
        }
      }}
      error={error}
      helperText={schema.description}
      disabled={disabled}
      fullWidth
      placeholder={schema.description || `Ingrese ${label.toLowerCase()}`}
      min={schema.minimum}
      max={schema.maximum}
    />
  );
}

/**
 * Boolean toggle field
 */
function BooleanField({ name, schema, value, onChange, error, disabled, required }: FieldProps) {
  const label = getFieldLabel(name, schema);
  const isChecked =
    value === undefined && schema.default !== undefined
      ? Boolean(schema.default)
      : Boolean(value);

  return (
    <div style={{ marginBottom: tokens.spacing[4] }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[3] }}>
        <button
          type="button"
          role="switch"
          aria-checked={isChecked}
          disabled={disabled}
          onClick={() => onChange(!isChecked)}
          style={{
            position: 'relative',
            width: 48,
            height: 26,
            borderRadius: tokens.borderRadius.full,
            border: 'none',
            backgroundColor: isChecked ? tokens.colors.primary[600] : tokens.colors.neutral[300],
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: `background-color ${tokens.transitions.fast}`,
            opacity: disabled ? 0.5 : 1,
            padding: 0,
            outline: 'none',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 3,
              left: isChecked ? 25 : 3,
              width: 20,
              height: 20,
              borderRadius: tokens.borderRadius.full,
              backgroundColor: '#ffffff',
              boxShadow: tokens.shadows.sm,
              transition: `left ${tokens.transitions.fast}`,
            }}
          />
        </button>
        <div>
          <label
            style={{
              fontFamily: tokens.typography.fontFamily.sans,
              fontSize: tokens.typography.fontSize.sm,
              fontWeight: tokens.typography.fontWeight.medium,
              color: tokens.colors.neutral[700],
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            onClick={() => !disabled && onChange(!isChecked)}
          >
            {label}{required ? ' *' : ''}
          </label>
          {schema.description && (
            <p
              style={{
                margin: 0,
                marginTop: tokens.spacing[1],
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.neutral[500],
              }}
            >
              {schema.description}
            </p>
          )}
        </div>
      </div>
      {error && (
        <span
          role="alert"
          style={{
            display: 'block',
            marginTop: tokens.spacing[1],
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.error.dark,
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * Select/dropdown field for enum values
 */
function SelectField({ name, schema, value, onChange, error, disabled, required }: FieldProps) {
  const label = getFieldLabel(name, schema);
  const options = schema.enum || [];
  const enumLabels = (schema as Record<string, unknown>)['x-enumLabels'] as Record<string, string> | undefined;
  const selectId = `field-${name}`;

  return (
    <div style={{ marginBottom: tokens.spacing[4] }}>
      <label
        htmlFor={selectId}
        style={{
          display: 'block',
          fontFamily: tokens.typography.fontFamily.sans,
          fontSize: tokens.typography.fontSize.sm,
          fontWeight: tokens.typography.fontWeight.medium,
          color: error ? tokens.colors.error.dark : tokens.colors.neutral[700],
          marginBottom: tokens.spacing[1],
        }}
      >
        {label}{required ? ' *' : ''}
      </label>
      <select
        id={selectId}
        value={String(value ?? '')}
        onChange={(e) => {
          const val = e.target.value;
          // Try to preserve the original type
          const originalOption = options.find((opt) => String(opt) === val);
          onChange(originalOption !== undefined ? originalOption : val);
        }}
        disabled={disabled}
        style={{
          width: '100%',
          padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
          fontFamily: tokens.typography.fontFamily.sans,
          fontSize: tokens.typography.fontSize.base,
          color: tokens.colors.neutral[900],
          backgroundColor: disabled ? tokens.colors.neutral[100] : tokens.colors.neutral[50],
          border: `1px solid ${error ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
          borderRadius: tokens.borderRadius.md,
          cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none',
          minHeight: '40px',
          transition: `border-color ${tokens.transitions.fast}`,
        }}
      >
        <option value="">Seleccione una opción</option>
        {options.map((opt, idx) => (
          <option key={idx} value={String(opt)}>
            {enumLabels?.[String(opt)] || String(opt)}
          </option>
        ))}
      </select>
      {schema.description && !error && (
        <span
          style={{
            display: 'block',
            marginTop: tokens.spacing[1],
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.neutral[500],
          }}
        >
          {schema.description}
        </span>
      )}
      {error && (
        <span
          role="alert"
          style={{
            display: 'block',
            marginTop: tokens.spacing[1],
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.error.dark,
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * Textarea field for long text
 */
function TextareaField({ name, schema, value, onChange, error, disabled, required }: FieldProps) {
  const label = getFieldLabel(name, schema);

  return (
    <div style={{ marginBottom: tokens.spacing[4] }}>
      <label
        style={{
          display: 'block',
          fontFamily: tokens.typography.fontFamily.sans,
          fontSize: tokens.typography.fontSize.sm,
          fontWeight: tokens.typography.fontWeight.medium,
          color: error ? tokens.colors.error.dark : tokens.colors.neutral[700],
          marginBottom: tokens.spacing[1],
        }}
      >
        {label}{required ? ' *' : ''}
      </label>
      <textarea
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={schema.description || `Ingrese ${label.toLowerCase()}`}
        rows={4}
        style={{
          width: '100%',
          padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
          fontFamily: tokens.typography.fontFamily.sans,
          fontSize: tokens.typography.fontSize.base,
          color: tokens.colors.neutral[900],
          backgroundColor: disabled ? tokens.colors.neutral[100] : tokens.colors.neutral[50],
          border: `1px solid ${error ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
          borderRadius: tokens.borderRadius.md,
          resize: 'vertical',
          outline: 'none',
          transition: `border-color ${tokens.transitions.fast}`,
        }}
      />
      {schema.description && !error && (
        <span
          style={{
            display: 'block',
            marginTop: tokens.spacing[1],
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.neutral[500],
          }}
        >
          {schema.description}
        </span>
      )}
      {error && (
        <span
          role="alert"
          style={{
            display: 'block',
            marginTop: tokens.spacing[1],
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.error.dark,
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * Checks if an array schema has exactly 2 simple string properties (key-value pair pattern).
 */
function isKeyValuePairSchema(schema: JSONSchema7): boolean {
  const itemSchema = schema.items as JSONSchema7 | undefined;
  if (!itemSchema || getPrimaryType(itemSchema.type) !== 'object') return false;
  const props = itemSchema.properties || {};
  const keys = Object.keys(props);
  if (keys.length !== 2) return false;
  return keys.every((k) => getPrimaryType(props[k].type) === 'string' && !props[k].enum);
}

/**
 * REST Catalog queryMappingEntries: param + description + per-row enabled switch.
 */
function isQueryMappingEntriesSchema(schema: JSONSchema7): boolean {
  const itemSchema = schema.items as JSONSchema7 | undefined;
  if (!itemSchema || getPrimaryType(itemSchema.type) !== 'object') return false;
  const props = itemSchema.properties || {};
  return (
    getPrimaryType(props.param?.type) === 'string'
    && getPrimaryType(props.description?.type) === 'string'
    && getPrimaryType(props.enabled?.type) === 'boolean'
  );
}

function InlineSwitch({
  checked,
  disabled,
  onChange,
  title,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={title || 'Activo para LLM'}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        width: 44,
        height: 24,
        borderRadius: tokens.borderRadius.full,
        border: 'none',
        backgroundColor: checked ? tokens.colors.primary[600] : tokens.colors.neutral[300],
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: `background-color ${tokens.transitions.fast}`,
        opacity: disabled ? 0.5 : 1,
        padding: 0,
        outline: 'none',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: checked ? 23 : 3,
          width: 18,
          height: 18,
          borderRadius: tokens.borderRadius.full,
          backgroundColor: '#ffffff',
          boxShadow: tokens.shadows.sm,
          transition: `left ${tokens.transitions.fast}`,
        }}
      />
    </button>
  );
}

/**
 * Parámetros de búsqueda REST Catalog — fila con switch LLM + param + descripción.
 */
function QueryMappingEntriesField({ name, schema, value, onChange, disabled, required }: FieldProps) {
  const label = getFieldLabel(name, schema);
  const itemSchema = schema.items as JSONSchema7;
  const enabledSchema = itemSchema.properties?.enabled;
  const items = Array.isArray(value) ? value : [];

  const addItem = () => {
    onChange([...items, { enabled: true, param: '', description: '' }]);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_: unknown, i: number) => i !== index));
  };

  const updateItem = (index: number, field: string, fieldValue: unknown) => {
    const next = items.map((item: unknown, i: number) => {
      if (i !== index) return item;
      return { ...(item as Record<string, unknown>), [field]: fieldValue };
    });
    onChange(next);
  };

  const isEntryEnabled = (item: Record<string, unknown>): boolean =>
    item.enabled === undefined
      ? enabledSchema?.default !== undefined
        ? Boolean(enabledSchema.default)
        : true
      : Boolean(item.enabled);

  return (
    <div style={{ marginBottom: tokens.spacing[4] }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: tokens.spacing[2] }}>
        <label
          style={{
            fontFamily: tokens.typography.fontFamily.sans,
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.medium,
            color: tokens.colors.neutral[700],
          }}
        >
          {label}{required ? ' *' : ''}
        </label>
        <button
          type="button"
          onClick={addItem}
          disabled={disabled}
          style={{
            padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
            fontFamily: tokens.typography.fontFamily.sans,
            fontSize: tokens.typography.fontSize.xs,
            fontWeight: tokens.typography.fontWeight.medium,
            color: tokens.colors.primary[700],
            backgroundColor: tokens.colors.primary[50],
            border: `1px solid ${tokens.colors.primary[200]}`,
            borderRadius: tokens.borderRadius.md,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          + Agregar
        </button>
      </div>
      {schema.description && (
        <p style={{ margin: 0, marginBottom: tokens.spacing[2], fontSize: tokens.typography.fontSize.sm, color: tokens.colors.neutral[500] }}>
          {schema.description}
        </p>
      )}

      {items.length > 0 && (
        <div style={{ display: 'flex', gap: tokens.spacing[2], marginBottom: tokens.spacing[1], alignItems: 'center' }}>
          <span style={{ width: 44, flexShrink: 0, fontSize: tokens.typography.fontSize.xs, fontWeight: tokens.typography.fontWeight.medium, color: tokens.colors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
            {enabledSchema?.title || 'LLM'}
          </span>
          <span style={{ flex: 1, fontSize: tokens.typography.fontSize.xs, fontWeight: tokens.typography.fontWeight.medium, color: tokens.colors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Parámetro
          </span>
          <span style={{ flex: 1, fontSize: tokens.typography.fontSize.xs, fontWeight: tokens.typography.fontWeight.medium, color: tokens.colors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Descripción para el LLM
          </span>
          <span style={{ width: 28, flexShrink: 0 }} />
        </div>
      )}

      {items.map((item: unknown, index: number) => {
        const itemData = (item || {}) as Record<string, unknown>;
        const rowEnabled = isEntryEnabled(itemData);
        return (
          <div key={index} style={{ display: 'flex', gap: tokens.spacing[2], marginBottom: tokens.spacing[2], alignItems: 'center' }}>
            <InlineSwitch
              checked={rowEnabled}
              disabled={disabled}
              title={enabledSchema?.title || 'Activo para LLM'}
              onChange={(next) => updateItem(index, 'enabled', next)}
            />
            <input
              type="text"
              value={String(itemData.param ?? '')}
              onChange={(e) => updateItem(index, 'param', e.target.value)}
              disabled={disabled}
              placeholder="q, category, minPrice…"
              style={{
                flex: 1,
                padding: `${tokens.spacing[2]} ${tokens.spacing[2]}`,
                fontFamily: tokens.typography.fontFamily.sans,
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.neutral[900],
                backgroundColor: disabled ? tokens.colors.neutral[100] : tokens.colors.neutral[50],
                border: `1px solid ${tokens.colors.neutral[300]}`,
                borderRadius: tokens.borderRadius.md,
                outline: 'none',
                opacity: rowEnabled ? 1 : 0.65,
              }}
            />
            <input
              type="text"
              value={String(itemData.description ?? '')}
              onChange={(e) => updateItem(index, 'description', e.target.value)}
              disabled={disabled}
              placeholder="Qué significa para el LLM"
              style={{
                flex: 1,
                padding: `${tokens.spacing[2]} ${tokens.spacing[2]}`,
                fontFamily: tokens.typography.fontFamily.sans,
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.neutral[900],
                backgroundColor: disabled ? tokens.colors.neutral[100] : tokens.colors.neutral[50],
                border: `1px solid ${tokens.colors.neutral[300]}`,
                borderRadius: tokens.borderRadius.md,
                outline: 'none',
                opacity: rowEnabled ? 1 : 0.65,
              }}
            />
            <button
              type="button"
              onClick={() => removeItem(index)}
              disabled={disabled}
              style={{
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: tokens.typography.fontSize.base,
                color: tokens.colors.error.dark,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: tokens.borderRadius.md,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 0.7,
                flexShrink: 0,
              }}
              title="Eliminar"
            >
              ×
            </button>
          </div>
        );
      })}

      {items.length === 0 && (
        <div
          style={{
            padding: tokens.spacing[3],
            textAlign: 'center',
            color: tokens.colors.neutral[400],
            fontSize: tokens.typography.fontSize.sm,
            border: `1px dashed ${tokens.colors.neutral[300]}`,
            borderRadius: tokens.borderRadius.md,
          }}
        >
          Sin parámetros. Hacé clic en "+ Agregar" para definir uno.
        </div>
      )}
    </div>
  );
}

/**
 * Compact key-value pair array — renders as inline rows with two inputs.
 * Used for simple 2-field string arrays (not queryMappingEntries).
 */
function KeyValuePairArrayField({ name, schema, value, onChange, disabled, required }: FieldProps) {
  const label = getFieldLabel(name, schema);
  const itemSchema = schema.items as JSONSchema7;
  const itemProperties = itemSchema.properties || {};
  const fieldNames = Object.keys(itemProperties);
  const items = Array.isArray(value) ? value : [];

  const addItem = () => {
    const newItem: Record<string, unknown> = {};
    for (const fn of fieldNames) newItem[fn] = '';
    onChange([...items, newItem]);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_: unknown, i: number) => i !== index));
  };

  const updateItem = (index: number, field: string, fieldValue: string) => {
    const next = items.map((item: unknown, i: number) => {
      if (i !== index) return item;
      return { ...(item as Record<string, unknown>), [field]: fieldValue };
    });
    onChange(next);
  };

  return (
    <div style={{ marginBottom: tokens.spacing[4] }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: tokens.spacing[2] }}>
        <label
          style={{
            fontFamily: tokens.typography.fontFamily.sans,
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.medium,
            color: tokens.colors.neutral[700],
          }}
        >
          {label}{required ? ' *' : ''}
        </label>
        <button
          type="button"
          onClick={addItem}
          disabled={disabled}
          style={{
            padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
            fontFamily: tokens.typography.fontFamily.sans,
            fontSize: tokens.typography.fontSize.xs,
            fontWeight: tokens.typography.fontWeight.medium,
            color: tokens.colors.primary[700],
            backgroundColor: tokens.colors.primary[50],
            border: `1px solid ${tokens.colors.primary[200]}`,
            borderRadius: tokens.borderRadius.md,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          + Agregar
        </button>
      </div>
      {schema.description && (
        <p style={{ margin: 0, marginBottom: tokens.spacing[2], fontSize: tokens.typography.fontSize.sm, color: tokens.colors.neutral[500] }}>
          {schema.description}
        </p>
      )}

      {/* Column headers */}
      {items.length > 0 && (
        <div style={{ display: 'flex', gap: tokens.spacing[2], marginBottom: tokens.spacing[1] }}>
          {fieldNames.map((fn) => (
            <span
              key={fn}
              style={{
                flex: 1,
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
                color: tokens.colors.neutral[500],
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {itemProperties[fn].title || fn}
            </span>
          ))}
          <span style={{ width: 28 }} /> {/* spacer for remove button */}
        </div>
      )}

      {items.map((item: unknown, index: number) => {
        const itemData = (item || {}) as Record<string, unknown>;
        return (
          <div key={index} style={{ display: 'flex', gap: tokens.spacing[2], marginBottom: tokens.spacing[2], alignItems: 'center' }}>
            {fieldNames.map((fn) => (
              <input
                key={fn}
                type="text"
                value={String(itemData[fn] ?? '')}
                onChange={(e) => updateItem(index, fn, e.target.value)}
                disabled={disabled}
                placeholder={itemProperties[fn].description || itemProperties[fn].title || fn}
                style={{
                  flex: 1,
                  padding: `${tokens.spacing[2]} ${tokens.spacing[2]}`,
                  fontFamily: tokens.typography.fontFamily.sans,
                  fontSize: tokens.typography.fontSize.sm,
                  color: tokens.colors.neutral[900],
                  backgroundColor: disabled ? tokens.colors.neutral[100] : tokens.colors.neutral[50],
                  border: `1px solid ${tokens.colors.neutral[300]}`,
                  borderRadius: tokens.borderRadius.md,
                  outline: 'none',
                }}
              />
            ))}
            <button
              type="button"
              onClick={() => removeItem(index)}
              disabled={disabled}
              style={{
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: tokens.typography.fontSize.base,
                color: tokens.colors.error.dark,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: tokens.borderRadius.md,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 0.7,
                flexShrink: 0,
              }}
              title="Eliminar"
            >
              ×
            </button>
          </div>
        );
      })}

      {items.length === 0 && (
        <div
          style={{
            padding: tokens.spacing[3],
            textAlign: 'center',
            color: tokens.colors.neutral[400],
            fontSize: tokens.typography.fontSize.sm,
            border: `1px dashed ${tokens.colors.neutral[300]}`,
            borderRadius: tokens.borderRadius.md,
          }}
        >
          Sin parámetros. Hacé clic en "+ Agregar" para definir uno.
        </div>
      )}
    </div>
  );
}

/**
 * Array of Objects field — renders a list of sub-forms with add/remove buttons.
 * Used for complex array fields like endpoints configuration.
 */
function ArrayOfObjectsField({ name, schema, value, onChange, disabled, required }: FieldProps) {
  const label = getFieldLabel(name, schema);
  const itemSchema = schema.items as JSONSchema7 | undefined;
  const items = Array.isArray(value) ? value : [];
  const itemProperties = itemSchema?.properties || {};
  const itemRequired = itemSchema?.required || [];

  const addItem = () => {
    const newItem: Record<string, unknown> = {};
    for (const [fieldName, fieldSchema] of Object.entries(itemProperties)) {
      const def = getDefaultValue(fieldSchema);
      if (def !== undefined && def !== '') {
        newItem[fieldName] = def;
      }
    }
    onChange([...items, newItem]);
  };

  const removeItem = (index: number) => {
    const next = items.filter((_: unknown, i: number) => i !== index);
    onChange(next);
  };

  const updateItem = (index: number, field: string, fieldValue: unknown) => {
    const next = items.map((item: unknown, i: number) => {
      if (i !== index) return item;
      return { ...(item as Record<string, unknown>), [field]: fieldValue };
    });
    onChange(next);
  };

  // Determine field order within each item
  const sortedItemFields = Object.keys(itemProperties).sort((a, b) => {
    const aReq = itemRequired.includes(a);
    const bReq = itemRequired.includes(b);
    if (aReq && !bReq) return -1;
    if (!aReq && bReq) return 1;
    return 0;
  });

  return (
    <div style={{ marginBottom: tokens.spacing[4] }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: tokens.spacing[2] }}>
        <label
          style={{
            fontFamily: tokens.typography.fontFamily.sans,
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.semibold,
            color: tokens.colors.neutral[700],
          }}
        >
          {label}{required ? ' *' : ''}
        </label>
        <button
          type="button"
          onClick={addItem}
          disabled={disabled}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.spacing[1],
            padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
            fontFamily: tokens.typography.fontFamily.sans,
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.medium,
            color: tokens.colors.primary[700],
            backgroundColor: tokens.colors.primary[50],
            border: `1px solid ${tokens.colors.primary[200]}`,
            borderRadius: tokens.borderRadius.md,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            transition: `all ${tokens.transitions.fast}`,
          }}
        >
          + Agregar
        </button>
      </div>
      {schema.description && (
        <p
          style={{
            margin: 0,
            marginBottom: tokens.spacing[3],
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.neutral[500],
          }}
        >
          {schema.description}
        </p>
      )}

      {items.length === 0 && (
        <div
          style={{
            padding: tokens.spacing[4],
            textAlign: 'center',
            color: tokens.colors.neutral[400],
            fontSize: tokens.typography.fontSize.sm,
            border: `1px dashed ${tokens.colors.neutral[300]}`,
            borderRadius: tokens.borderRadius.md,
          }}
        >
          No hay endpoints configurados. Hacé clic en "+ Agregar" para crear uno.
        </div>
      )}

      {items.map((item: unknown, index: number) => {
        const itemData = (item || {}) as Record<string, unknown>;
        const itemName = itemData.name ? String(itemData.name) : `#${index + 1}`;

        return (
          <div
            key={index}
            style={{
              marginBottom: tokens.spacing[3],
              padding: tokens.spacing[4],
              border: `1px solid ${tokens.colors.neutral[200]}`,
              borderRadius: tokens.borderRadius.lg,
              backgroundColor: tokens.colors.neutral[50],
            }}
          >
            {/* Header with name and remove button */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: tokens.spacing[3],
                paddingBottom: tokens.spacing[2],
                borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
              }}
            >
              <span
                style={{
                  fontFamily: tokens.typography.fontFamily.sans,
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  color: tokens.colors.neutral[800],
                }}
              >
                {itemName}
              </span>
              <button
                type="button"
                onClick={() => removeItem(index)}
                disabled={disabled}
                style={{
                  padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                  fontFamily: tokens.typography.fontFamily.sans,
                  fontSize: tokens.typography.fontSize.xs,
                  color: tokens.colors.error.dark,
                  backgroundColor: 'transparent',
                  border: `1px solid ${tokens.colors.error.light}`,
                  borderRadius: tokens.borderRadius.md,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                  transition: `all ${tokens.transitions.fast}`,
                }}
              >
                Eliminar
              </button>
            </div>

            {/* Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
              {sortedItemFields.map((fieldName) => {
                const fieldSchema = itemProperties[fieldName];
                const fieldValue = itemData[fieldName];
                const isFieldRequired = itemRequired.includes(fieldName);

                // Skip hidden fields
                if ((fieldSchema as Record<string, unknown>)?.['x-hidden']) return null;

                return renderField(
                  `${name}[${index}].${fieldName}`,
                  fieldSchema,
                  fieldValue,
                  (val) => updateItem(index, fieldName, val),
                  undefined,
                  disabled || false,
                  isFieldRequired,
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Free-form object (additionalProperties only) as JSON textarea.
 */
function JsonObjectField({ name, schema, value, onChange, error, disabled, required }: FieldProps) {
  const label = getFieldLabel(name, schema);
  const textValue =
    typeof value === 'string'
      ? value
      : value && typeof value === 'object' && !Array.isArray(value)
        ? JSON.stringify(value, null, 2)
        : '';

  return (
    <TextareaField
      name={name}
      schema={{
        ...schema,
        title: label,
        description: schema.description || 'Objeto JSON (clave → string)',
      }}
      value={textValue}
      onChange={(v) => {
        const raw = String(v).trim();
        if (!raw) {
          onChange(undefined);
          return;
        }
        try {
          onChange(JSON.parse(raw));
        } catch {
          onChange(v);
        }
      }}
      error={error}
      disabled={disabled}
      required={required}
    />
  );
}

/**
 * Nested object with declared properties (e.g. catalogLookup).
 */
function ObjectNestedField({ name, schema, value, onChange, error, disabled, required }: FieldProps) {
  const label = getFieldLabel(name, schema);
  const obj =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const properties = schema.properties || {};
  const nestedRequired = schema.required || [];

  return (
    <div
      style={{
        marginBottom: tokens.spacing[4],
        padding: tokens.spacing[4],
        border: `1px solid ${tokens.colors.neutral[200]}`,
        borderRadius: tokens.borderRadius.lg,
        backgroundColor: tokens.colors.neutral[50],
      }}
    >
      <div
        style={{
          fontFamily: tokens.typography.fontFamily.sans,
          fontSize: tokens.typography.fontSize.sm,
          fontWeight: tokens.typography.fontWeight.semibold,
          color: tokens.colors.neutral[800],
          marginBottom: tokens.spacing[2],
        }}
      >
        {label}{required ? ' *' : ''}
      </div>
      {schema.description && (
        <p
          style={{
            margin: 0,
            marginBottom: tokens.spacing[3],
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.neutral[500],
          }}
        >
          {schema.description}
        </p>
      )}
      {Object.entries(properties).map(([fieldName, fieldSchema]) => {
        const fieldValue = obj[fieldName];
        const isFieldRequired = nestedRequired.includes(fieldName);
        return renderField(
          `${name}.${fieldName}`,
          fieldSchema,
          fieldValue,
          (val) => onChange({ ...obj, [fieldName]: val }),
          undefined,
          disabled ?? false,
          isFieldRequired,
        );
      })}
      {error && (
        <span
          role="alert"
          style={{
            display: 'block',
            marginTop: tokens.spacing[1],
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.error.dark,
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * Renders a field based on its schema type
 */
function renderField(
  name: string,
  schema: JSONSchema7,
  value: unknown,
  onChange: (value: unknown) => void,
  error: string | undefined,
  disabled: boolean,
  required: boolean
): React.ReactNode {
  const type = getPrimaryType(schema.type);

  // Skip hidden fields
  if ((schema as Record<string, unknown>)?.['x-hidden']) {
    return null;
  }

  // If there's an enum, render a select
  if (schema.enum && schema.enum.length > 0) {
    return (
      <SelectField
        key={name}
        name={name}
        schema={schema}
        value={value}
        onChange={onChange}
        error={error}
        disabled={disabled}
        required={required}
      />
    );
  }

  switch (type) {
    case 'boolean':
      return (
        <BooleanField
          key={name}
          name={name}
          schema={schema}
          value={value}
          onChange={onChange}
          error={error}
          disabled={disabled}
          required={required}
        />
      );
    case 'number':
    case 'integer':
      return (
        <NumberField
          key={name}
          name={name}
          schema={schema}
          value={value}
          onChange={onChange}
          error={error}
          disabled={disabled}
          required={required}
        />
      );
    case 'array': {
      const itemSchema = schema.items as JSONSchema7 | undefined;
      if (itemSchema && getPrimaryType(itemSchema.type) === 'object' && itemSchema.properties) {
        if (isQueryMappingEntriesSchema(schema)) {
          return (
            <QueryMappingEntriesField
              key={name}
              name={name}
              schema={schema}
              value={value}
              onChange={onChange}
              error={error}
              disabled={disabled}
              required={required}
            />
          );
        }
        // Use compact key-value pair editor for 2-field schemas
        if (isKeyValuePairSchema(schema)) {
          return (
            <KeyValuePairArrayField
              key={name}
              name={name}
              schema={schema}
              value={value}
              onChange={onChange}
              error={error}
              disabled={disabled}
              required={required}
            />
          );
        }
        return (
          <ArrayOfObjectsField
            key={name}
            name={name}
            schema={schema}
            value={value}
            onChange={onChange}
            error={error}
            disabled={disabled}
            required={required}
          />
        );
      }
      // Fallback: render as textarea (JSON)
      return (
        <TextareaField
          key={name}
          name={name}
          schema={{ ...schema, title: schema.title, description: schema.description || 'Formato JSON' }}
          value={typeof value === 'string' ? value : JSON.stringify(value ?? [], null, 2)}
          onChange={(v) => {
            try {
              onChange(JSON.parse(String(v)));
            } catch {
              onChange(v);
            }
          }}
          error={error}
          disabled={disabled}
          required={required}
        />
      );
    }
    case 'object': {
      const props = schema.properties || {};
      if (Object.keys(props).length > 0) {
        return (
          <ObjectNestedField
            key={name}
            name={name}
            schema={schema}
            value={value}
            onChange={onChange}
            error={error}
            disabled={disabled}
            required={required}
          />
        );
      }
      return (
        <JsonObjectField
          key={name}
          name={name}
          schema={schema}
          value={value}
          onChange={onChange}
          error={error}
          disabled={disabled}
          required={required}
        />
      );
    }
    case 'string':
      // Use textarea for long text fields
      if (schema.maxLength && schema.maxLength > 200) {
        return (
          <TextareaField
            key={name}
            name={name}
            schema={schema}
            value={value}
            onChange={onChange}
            error={error}
            disabled={disabled}
            required={required}
          />
        );
      }
      return (
        <TextField
          key={name}
          name={name}
          schema={schema}
          value={value}
          onChange={onChange}
          error={error}
          disabled={disabled}
          required={required}
        />
      );
    default:
      // Default to text field for unknown types
      return (
        <TextField
          key={name}
          name={name}
          schema={schema}
          value={value}
          onChange={onChange}
          error={error}
          disabled={disabled}
          required={required}
        />
      );
  }
}

/**
 * DynamicSchemaForm component
 *
 * Renders a form based on a JSON Schema definition. Supports:
 * - String, number, integer, and boolean fields
 * - Enum fields rendered as dropdowns
 * - Required field validation
 * - Field-level error display
 * - Default values from schema
 *
 * @example
 * ```tsx
 * const schema = {
 *   type: 'object',
 *   properties: {
 *     apiKey: { type: 'string', title: 'API Key', minLength: 1 },
 *     enabled: { type: 'boolean', default: true },
 *     mode: { type: 'string', enum: ['development', 'production'] },
 *   },
 *   required: ['apiKey'],
 * };
 *
 * <DynamicSchemaForm
 *   schema={schema}
 *   values={formValues}
 *   onChange={setFormValues}
 *   errors={validationErrors}
 * />
 * ```
 */
export function DynamicSchemaForm({
  schema,
  values,
  onChange,
  errors = [],
  disabled = false,
}: DynamicSchemaFormProps) {
  const properties = schema.properties || {};
  const required = schema.required || [];

  // Create a map of errors by field path for quick lookup
  const errorMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const error of errors) {
      // Normalize path: /fieldName -> fieldName
      const fieldName = error.path.replace(/^\//, '');
      map.set(fieldName, error.message);
    }
    return map;
  }, [errors]);

  // Handle field value change
  const handleFieldChange = useCallback(
    (fieldName: string, fieldValue: unknown) => {
      onChange({
        ...values,
        [fieldName]: fieldValue,
      });
    },
    [values, onChange]
  );

  // Get sorted field names (required fields first, then by schema order, skip hidden)
  const sortedFieldNames = useMemo(() => {
    return Object.keys(properties)
      .filter((name) => !(properties[name] as Record<string, unknown>)?.['x-hidden'])
      .sort((a, b) => {
        const aRequired = required.includes(a);
        const bRequired = required.includes(b);
        if (aRequired && !bRequired) return -1;
        if (!aRequired && bRequired) return 1;
        return 0;
      });
  }, [properties, required]);

  // If no properties, show empty state
  if (sortedFieldNames.length === 0) {
    return (
      <div
        style={{
          padding: tokens.spacing[4],
          textAlign: 'center',
          color: tokens.colors.neutral[500],
          fontSize: tokens.typography.fontSize.sm,
        }}
      >
        Este plugin no requiere configuración adicional.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
      {sortedFieldNames.map((fieldName) => {
        const fieldSchema = properties[fieldName];
        const fieldValue = values[fieldName];
        const fieldError = errorMap.get(fieldName);
        const isRequired = required.includes(fieldName);

        return renderField(
          fieldName,
          fieldSchema,
          fieldValue,
          (value) => handleFieldChange(fieldName, value),
          fieldError,
          disabled,
          isRequired
        );
      })}
    </div>
  );
}

export default DynamicSchemaForm;
